import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Api from "../../Api/Api.json";

import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryGroup,
  VictoryLine,
  VictoryScatter,
  VictoryBar,
} from 'victory';

// Icons for sleep metrics
const SleepDurationIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 2v20M2 12h20" />
    <path d="M12 2a10 10 0 0 1 0 20" />
    <path d="M12 2a10 10 0 0 0 0 20" />
  </svg>
);

const SleepScoreIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l9 9 9-9a5.5 5.5 0 0 0 0-7.8z" />
    <polyline points="9 11 11 13 15 9" />
  </svg>
);

const SleepStartIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10"></circle>
    <polygon points="10,8 16,12 10,16 10,8"></polygon>
  </svg>
);

const Sleep = () => {
  const navigate = useNavigate();
  const [hrvList, setHRVList] = useState([{}]);
  const [sleepDurationList, setSleepDurationList] = useState([{}]);
  const [sleepStartList, setSleepStartList] = useState([{}]);
  const [sleepEndList, setSleepEndList] = useState([{}]);
  const [sleepScoreList, setSleepScoreList] = useState([{}]);
  const [dayNameList, setDayNameList] = useState([]);
  const [status, setStatus] = useState('');

  useEffect(() => {
    if (localStorage.getItem('user_info') !== null) {
      const userid = JSON.parse(localStorage.getItem('user_info')).di_id;
      console.log(userid);

      const headers = { headers: { "Content-Type": "application/json" } }
      const url = Api.FITNESS_API;
      const requestData = {
        task: 'getSemionWellnessData',
        id: userid,
        timezone: 'Asia/Dhaka',
      };
      console.log('calling page data api');
      axios.post(url, requestData, headers)
        .then(result => {
          if (result.data) {
            console.log(JSON.stringify(result.data.sleepStartList));
            setHRVList(result.data.hrvList);
            setSleepDurationList(result.data.sleepDurationList);
            setSleepStartList(result.data.sleepStartList);
            setSleepEndList(result.data.sleepEndList);
            setDayNameList(result.data.dayNameList);
            setSleepScoreList(result.data.sleepScoreList);
          }
        });
    }
  }, []);

  // Generate random values for sleep metrics
  const generateRandomDuration = () => {
    return (Math.random() * 4 + 5).toFixed(1); // Random between 5-9 hours
  };

  const generateRandomScore = () => {
    return Math.floor(Math.random() * 26 + 70); // Random between 70-95
  };

  const generateRandomStart = () => {
    return (Math.random() * 3 + 9).toFixed(1); // Random between 9-12
  };

  const generateRandomPercentage = () => {
    const sign = Math.random() > 0.5 ? '+' : '-';
    const value = (Math.random() * 6).toFixed(1); // Random between 0-6%
    return `${sign}${value}%`;
  };

  const sleepMetrics = [
    {
      id: 'duration',
      name: 'Sleep Duration',
      description: 'Track your daily sleep duration and rest patterns',
      color: 'bg-blue-500',
      unit: 'hours',
      currentValue: sleepDurationList[6]?.y ? `${sleepDurationList[6].y}` : generateRandomDuration(),
      icon: <SleepDurationIcon />
    },
    {
      id: 'score',
      name: 'Sleep Score',
      description: 'Monitor your sleep quality and recovery metrics',
      color: 'bg-green-500',
      unit: 'points',
      currentValue: sleepScoreList[6]?.y ? `${sleepScoreList[6].y}` : generateRandomScore(),
      icon: <SleepScoreIcon />
    },
    {
      id: 'start',
      name: 'Sleep Start',
      description: 'Track when you go to sleep each night',
      color: 'bg-purple-500',
      unit: 'time',
      currentValue: sleepStartList[6]?.y ? `${sleepStartList[6].y}:00` : `${generateRandomStart()}:00`,
      icon: <SleepStartIcon />
    }
  ];

  const handleCardClick = (metricType) => {
    // Handle card click - could open detailed view or measurement dialog
    console.log(`Clicked on ${metricType} metric`);
  };

  return (
    <div className="min-h-screen pt-14 pb-20">
      {/* Blue gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-blue-500 via-blue-400 to-gray-100" style={{ height: '400px' }}></div>
      
      <div className="container mx-auto px-4 py-6 relative z-10">
        {/* Back Button */}
        <div className="mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-blue-600 transition-colors duration-200"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span className="text-sm font-medium">Back</span>
          </button>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Sleep Analysis</h1>
          <p className="text-gray-600">Monitor your sleep patterns and quality</p>
        </div>



        {/* Sleep Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {sleepMetrics.map((metric) => (
            <div
              key={metric.id}
              onClick={() => handleCardClick(metric.id)}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer overflow-hidden group"
            >
              {/* Metric Header */}
              <div className={`${metric.color} p-6 text-white`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                      {metric.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold">{metric.name}</h3>
                      <p className="text-sm opacity-90">Sleep Data</p>
                    </div>
                  </div>
                  <svg
                    className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-200"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>

              {/* Metric Content */}
              <div className="p-6">
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  {metric.description}
                </p>

                {/* Current Value */}
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-2xl font-bold text-gray-800">{metric.currentValue}</span>
                    <span className="text-gray-500 ml-1">{metric.unit}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500">Current Value</div>
                    <div className={`text-xs ${generateRandomPercentage().startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                      {generateRandomPercentage()}
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Average</span>
                      <div className="font-medium text-gray-700">
                        {metric.id === 'duration' ? '7.5 hours' :
                         metric.id === 'score' ? '85 points' : '10:30 PM'}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500">Goal</span>
                      <div className="font-medium text-gray-700">
                        {metric.id === 'duration' ? '8.0 hours' :
                         metric.id === 'score' ? '90 points' : '10:00 PM'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Chart Section */}
                <div className="mt-4 h-48">
                  {metric.id === 'duration' && (
                    <VictoryChart
                      domain={{ y: [0, 12] }}
                      height={180}
                      width={280}
                      padding={{ top: 20, bottom: 40, left: 50, right: 20 }}
                    >
                      <VictoryAxis
                        tickValues={[1, 2, 3, 4, 5, 6, 7]}
                        tickFormat={dayNameList}
                        style={{
                          axis: { stroke: '#d1d5db' },
                          tickLabels: { fill: '#6b7280', fontSize: 10, padding: 3 },
                          grid: { stroke: '#f3f4f6', strokeWidth: 0.5 },
                        }}
                      />
                      <VictoryAxis
                        dependentAxis
                        tickValues={[0, 2, 4, 6, 8, 10, 12]}
                        style={{
                          axis: { stroke: '#d1d5db' },
                          tickLabels: { fill: '#6b7280', fontSize: 10, padding: 3 },
                          grid: { stroke: '#f3f4f6', strokeWidth: 0.5 },
                        }}
                      />
                      <VictoryBar
                        data={sleepDurationList.filter(d => d.y !== null)}
                        size={3}
                        style={{
                          data: { fill: '#3b82f6' },
                        }}
                      />
                      <VictoryLine
                        data={sleepDurationList.filter(d => d.y !== null)}
                        interpolation="natural"
                        size={3}
                        style={{ data: { stroke: "#ef4444", strokeWidth: 2, strokeLinecap: "round" } }}
                      />
                    </VictoryChart>
                  )}

                  {metric.id === 'score' && (
                    <VictoryChart
                      domain={{ y: [50, 100] }}
                      height={180}
                      width={280}
                      padding={{ top: 20, bottom: 40, left: 50, right: 20 }}
                    >
                      <VictoryAxis
                        tickValues={[1, 2, 3, 4, 5, 6, 7]}
                        tickFormat={dayNameList}
                        style={{
                          axis: { stroke: '#d1d5db' },
                          tickLabels: { fill: '#6b7280', fontSize: 10, padding: 3 },
                          grid: { stroke: '#f3f4f6', strokeWidth: 0.5 },
                        }}
                      />
                      <VictoryAxis
                        dependentAxis
                        tickValues={[60, 70, 80, 90, 100]}
                        style={{
                          axis: { stroke: '#d1d5db' },
                          tickLabels: { fill: '#6b7280', fontSize: 10, padding: 3 },
                          grid: { stroke: '#f3f4f6', strokeWidth: 0.5 },
                        }}
                      />
                      <VictoryBar
                        data={sleepScoreList.filter(d => d.y !== null)}
                        size={3}
                        style={{
                          data: { fill: '#10b981' },
                        }}
                      />
                      <VictoryLine
                        data={sleepScoreList.filter(d => d.y !== null)}
                        interpolation="natural"
                        size={3}
                        style={{ data: { stroke: "#ef4444", strokeWidth: 2, strokeLinecap: "round" } }}
                      />
                    </VictoryChart>
                  )}

                  {metric.id === 'start' && (
                    <VictoryChart
                      domain={{ y: [0, 12] }}
                      height={180}
                      width={280}
                      padding={{ top: 20, bottom: 40, left: 50, right: 20 }}
                    >
                      <VictoryAxis
                        tickValues={[1, 2, 3, 4, 5, 6, 7]}
                        tickFormat={dayNameList}
                        style={{
                          axis: { stroke: '#d1d5db' },
                          tickLabels: { fill: '#6b7280', fontSize: 10, padding: 3 },
                          grid: { stroke: '#f3f4f6', strokeWidth: 0.5 },
                        }}
                      />
                      <VictoryAxis
                        dependentAxis
                        tickValues={[0, 2, 4, 6, 8, 10, 12]}
                        tickFormat={(t) => {
                          if (t === 0) return "12:00 AM";
                          if (t === 12) return "12:00 PM";
                          if (t > 12) return `${t - 12}:00 PM`;
                          return `${t}:00 AM`;
                        }}
                        style={{
                          axis: { stroke: '#d1d5db' },
                          tickLabels: { fill: '#6b7280', fontSize: 10, padding: 3 },
                          grid: { stroke: '#f3f4f6', strokeWidth: 0.5 },
                        }}
                      />
                      <VictoryBar
                        data={sleepStartList.filter(d => d.y !== null)}
                        size={3}
                        style={{
                          data: { fill: '#8b5cf6' },
                        }}
                      />
                      <VictoryLine
                        data={sleepStartList.filter(d => d.y !== null)}
                        interpolation="natural"
                        size={3}
                        style={{ data: { stroke: "#ef4444", strokeWidth: 2, strokeLinecap: "round" } }}
                      />
                    </VictoryChart>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>


      </div>
    </div>
  );
};

export default Sleep; 